import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import Chip from '@mui/material/Chip';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateAssessmentMarkingAnswer } from '../../../../../../store/actions/dataEntryAssessmentMarkingActions';
import './MarkinVariants.css';

const MAX_OPTIONS = 10;

const MarkingMenu = memo(({ id, question, record, onChangeAlert, disabled }) => {
  const { answer = [], type, options = [], questionId } = question;
  const dispatch = useDispatch();
  const showActions = type?.value !== 'singlechoice';
  const [selectedOptions, setSelectedOptions] = useState(answer);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState();
  const [search, setSearch] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options ?? []);
  const hasValue = answer && answer?.length > 0;

  const handleClick = (newPlacement) => (event) => {
    event.preventDefault();
    setAnchorEl(event?.currentTarget);
    setOpen(true);
    setPlacement(newPlacement);
  };

  const handleClose = (evt) => {
    evt?.stopPropagation();
    setOpen(false);
    setSelectedOptions(answer);
  };

  const handleMenuItemClick = (event, value) => {
    event.stopPropagation();

    if (type?.value === 'singlechoice') {
      dispatch(
        updateAssessmentMarkingAnswer({
          recordId: record?._id,
          code: record?.code,
          questionId,
          answer: [value]
        })
      );
      setOpen(false);
      onChangeAlert();
    } else {
      setSelectedOptions((prev) =>
        prev.includes(value) ? prev.filter((option) => option !== value) : [...prev, value]
      );
    }
  };

  const onCancel = (evt) => {
    evt.stopPropagation();
    setSelectedOptions(answer);
    handleClose(evt);
  };

  const saveOnRedux = () => {
    dispatch(
      updateAssessmentMarkingAnswer({
        recordId: record?._id,
        code: record?.code,
        questionId,
        answer: selectedOptions
      })
    );
    onChangeAlert();
  };

  const onSave = (evt) => {
    evt.stopPropagation();
    saveOnRedux();
    handleClose(evt);
  };

  const onKeyUp = (evt) => {
    if (evt?.keyCode === 13 || evt?.key === 'Enter') {
      onSave(evt);
    }
  };

  const handleSearch = useCallback(
    (value) => {
      setSearch(value);

      if (value.trim() !== '') {
        const filtered = options?.filter((option) => {
          return option?.option?.toLowerCase().includes(value.toLowerCase());
        });
        setFilteredOptions(filtered);
      } else {
        setFilteredOptions(options);
      }
    },
    [options]
  );

  const handleClearValue = useCallback(() => {
    setSelectedOptions([]);
    dispatch(
      updateAssessmentMarkingAnswer({
        recordId: record?._id,
        code: record?.code,
        questionId,
        answer: []
      })
    );
  }, []);

  const getAnswerLabel = useMemo(() => {
    return options
      .filter((option) => selectedOptions.includes(option?.value))
      .map((option) => option?.option)
      .join(', ');
  }, [options, selectedOptions]);

  const getInputType = useMemo(() => {
    const inputComponents = {
      multiplechoice: filteredOptions.map((item) => (
        <MenuItem
          className='marking-menu__content__item'
          key={item?.value}
          onClick={(evt) => handleMenuItemClick(evt, item?.value)}
          onKeyUp={onKeyUp}
        >
          <FormControlLabel
            control={
              <Checkbox style={{ color: '#1e88e5' }} checked={selectedOptions.includes(item?.value)} />
            }
            label={item?.option}
          />
        </MenuItem>
      )),
      singlechoice: filteredOptions.map((item) => (
        <MenuItem
          className='marking-menu__content__item'
          key={item?.value}
          onClick={(evt) => handleMenuItemClick(evt, item?.value)}
        >
          <FormControlLabel
            control={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <Radio checked={selectedOptions[0] === item?.value} style={{ display: 'none' }} />
                {selectedOptions[0] === item?.value && (
                  <span style={{ color: '#1e88e5', marginRight: '8px' }}>
                    <FontAwesomeIcon icon='check' />
                  </span>
                )}
              </span>
            }
            label={item?.option}
          />
        </MenuItem>
      ))
    };
    return inputComponents[type?.value] || null;
  }, [type, filteredOptions, selectedOptions]);

  useEffect(() => {
    setSelectedOptions(answer);
  }, [answer]);

  useEffect(() => {
    if (!open) {
      setFilteredOptions(options);
      setSearch('');
    }
  }, [open]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div
        id={id}
        aria-describedby={id}
        onClick={disabled ? undefined : handleClick('bottom')}
        style={{ position: 'relative' }}
      >
        <Chip
          style={{ width: 100 }}
          className={`clickeable text-clickeable chip-clickeable ${answer.length ? '' : 'bg-fourth-orange'} ${
            open ? 'selected-chip' : ''
          }`}
          label={getAnswerLabel}
          disabled={disabled}
          {...(hasValue
            ? {
                onDelete: (evt) => {
                  evt.stopPropagation();
                  handleClearValue();
                }
              }
            : {})}
        />

        <Box sx={{ position: 'relative' }}>
          <Popper open={open} anchorEl={anchorEl} placement={placement} transition className='marking-menu'>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={100}>
                <Paper>
                  {options?.length > MAX_OPTIONS && (
                    <Box className='options__search-bar'>
                      <TextField
                        id={`short-answer-${questionId}`}
                        autoFocus
                        name='search'
                        value={search ?? ''}
                        placeholder='Search'
                        onChange={(evt) => handleSearch(evt.target.value)}
                        inputProps={{
                          maxLength: 25,
                          spellCheck: false
                        }}
                        style={{ mr: 1, width: '100%', height: '40px' }}
                        className='custom-text-field'
                        variant='outlined'
                      />
                    </Box>
                  )}
                  <div className='marking-menu__content'>
                    {getInputType}
                    {showActions && (
                      <div className='marking-menu__content__actions'>
                        <Button
                          className='button-cancel'
                          size='small'
                          onClick={onCancel}
                          startIcon={
                            <FontAwesomeIcon
                              icon={['fas', 'xmark']}
                              style={{ width: '20px', height: '15px' }}
                            />
                          }
                        />
                        {selectedOptions.length > 0 && (
                          <Button
                            className='button-save'
                            size='small'
                            onClick={onSave}
                            startIcon={
                              <FontAwesomeIcon icon='check' style={{ width: '20px', height: '14px' }} />
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>
      </div>
    </ClickAwayListener>
  );
});

export default MarkingMenu;
